import { LocationModel } from '../../../locations/location.model';
import { AddCameraWizardModels } from '../models/wizard.models';

export namespace WizardCameraCsvUtil {
    export enum CsvImportColumn {
        Name = 'name',
        IpAddress = 'ipAddress',
        Port = 'port',
        Username = 'username',
        Password = 'password',
        MainUrl = 'mainUrl',
        SubUrl = 'subUrl',
        Protocol = 'protocol',
    }

    export interface CsvParseResult {
        rows: AddCameraWizardModels.ManualCsvImportRow[];
        errors: string[];
    }

    const COLUMN_ALIASES: Record<CsvImportColumn, string[]> = {
        [CsvImportColumn.Name]: ['name', 'camera name'],
        [CsvImportColumn.IpAddress]: ['ip', 'ip address'],
        [CsvImportColumn.Port]: ['port'],
        [CsvImportColumn.Username]: ['username'],
        [CsvImportColumn.Password]: ['password'],
        [CsvImportColumn.MainUrl]: ['rtsp main', 'rtsp main path', 'path', 'rtsp main path'],
        [CsvImportColumn.SubUrl]: ['rtsp sub', 'rtsp sub path', 'substream rtsp path', 'sub path'],
        [CsvImportColumn.Protocol]: ['protocol'],
    };

    const REQUIRED_COLUMNS: CsvImportColumn[] = [
        CsvImportColumn.IpAddress,
        CsvImportColumn.Username,
        CsvImportColumn.Password,
        CsvImportColumn.MainUrl,
    ];

    const IP_PATTERN = /^(\d{1,3}\.){3}\d{1,3}$/;

    export function parseCameraImportCsv(content: string): CsvParseResult {
        const lines = splitCsvLines(content);
        if (!lines.length) {
            return { rows: [], errors: ['ADD_CAMERA_WIZARD.CSV.ERRORS.EMPTY_FILE'] };
        }

        const headerLine = lines[0];
        const delimiter = detectDelimiter(headerLine);
        const headers = parseCsvLine(headerLine, delimiter).map(normalizeHeader);
        const columnMap = mapHeaders(headers);

        const missing = REQUIRED_COLUMNS.filter((col) => columnMap[col] === undefined);
        if (missing.length) {
            return {
                rows: [],
                errors: [`ADD_CAMERA_WIZARD.CSV.ERRORS.MISSING_COLUMNS:${missing.join(',')}`],
            };
        }

        const rows: AddCameraWizardModels.ManualCsvImportRow[] = [];
        const errors: string[] = [];

        for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
            const line = lines[lineIndex].trim();
            if (!line) {
                continue;
            }

            const values = parseCsvLine(line, delimiter);
            const getValue = (column: CsvImportColumn): string =>
                (values[columnMap[column]!] ?? '').trim();

            const ipAddress = getValue(CsvImportColumn.IpAddress);
            const username = getValue(CsvImportColumn.Username);
            const password = getValue(CsvImportColumn.Password);
            const mainUrl = normalizeRtspPath(getValue(CsvImportColumn.MainUrl));
            const subUrl = normalizeRtspPath(getValue(CsvImportColumn.SubUrl));
            const name = getValue(CsvImportColumn.Name) || ipAddress;
            const portRaw = getValue(CsvImportColumn.Port);
            const port = portRaw ? Number(portRaw) : 554;
            const protocol = parseProtocol(getValue(CsvImportColumn.Protocol));

            const rowNumber = lineIndex + 1;
            if (!ipAddress || !IP_PATTERN.test(ipAddress)) {
                errors.push(`ADD_CAMERA_WIZARD.CSV.ERRORS.INVALID_IP:${rowNumber}`);
                continue;
            }
            if (!username || !password) {
                errors.push(`ADD_CAMERA_WIZARD.CSV.ERRORS.MISSING_CREDENTIALS:${rowNumber}`);
                continue;
            }
            if (!mainUrl) {
                errors.push(`ADD_CAMERA_WIZARD.CSV.ERRORS.MISSING_MAIN_PATH:${rowNumber}`);
                continue;
            }
            if (portRaw && (!Number.isFinite(port) || port < 1 || port > 65535)) {
                errors.push(`ADD_CAMERA_WIZARD.CSV.ERRORS.INVALID_PORT:${rowNumber}`);
                continue;
            }

            rows.push({
                name,
                ipAddress,
                port,
                username,
                password,
                mainUrl,
                subUrl,
                protocol,
            });
        }

        if (!rows.length && !errors.length) {
            errors.push('ADD_CAMERA_WIZARD.CSV.ERRORS.NO_DATA_ROWS');
        }

        return { rows, errors };
    }

    export function buildCredentialsFromCsvRows(
        rows: AddCameraWizardModels.ManualCsvImportRow[],
    ): LocationModel.Credentials[] {
        const groups = new Map<string, LocationModel.Credentials>();

        rows.forEach((row) => {
            const protocol = row.protocol ?? LocationModel.RtspProtocol.UDP;
            const port = row.port ?? 554;
            const key = `${row.username}|${row.password}|${port}|${protocol}`;
            const urlKey = `${row.mainUrl}|${row.subUrl ?? ''}`;

            if (!groups.has(key)) {
                groups.set(key, {
                    username: row.username,
                    password: row.password,
                    port,
                    protocol,
                    urls: [],
                    showPassword: false,
                    nvrVendor: 'hikvision',
                    subStream: !!row.subUrl,
                    channelCount: 4,
                });
            }

            const credential = groups.get(key)!;
            const hasUrl = (credential.urls ?? []).some(
                (url) => `${url.mainUrl}|${url.subUrl ?? ''}` === urlKey,
            );
            if (!hasUrl) {
                credential.urls = [...(credential.urls ?? []), { mainUrl: row.mainUrl, subUrl: row.subUrl ?? '' }];
            }
            credential.subStream = credential.subStream || !!row.subUrl;
        });

        return Array.from(groups.values());
    }

    function splitCsvLines(content: string): string[] {
        const normalized = content.replace(/^\uFEFF/, '');
        return normalized.split(/\r?\n/).filter((line, index, arr) => line.trim() || index < arr.length - 1);
    }

    function detectDelimiter(headerLine: string): string {
        const commaCount = (headerLine.match(/,/g) ?? []).length;
        const semicolonCount = (headerLine.match(/;/g) ?? []).length;
        return semicolonCount > commaCount ? ';' : ',';
    }

    function normalizeHeader(header: string): string {
        return header.trim().toLowerCase().replace(/\s+/g, ' ');
    }

    function mapHeaders(headers: string[]): Partial<Record<CsvImportColumn, number>> {
        const columnMap: Partial<Record<CsvImportColumn, number>> = {};

        headers.forEach((header, index) => {
            (Object.entries(COLUMN_ALIASES) as Array<[CsvImportColumn, string[]]>).forEach(([column, aliases]) => {
                if (aliases.includes(header)) {
                    columnMap[column] = index;
                }
            });
        });

        return columnMap;
    }

    function parseCsvLine(line: string, delimiter: string): string[] {
        const values: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
                continue;
            }

            if (char === delimiter && !inQuotes) {
                values.push(current);
                current = '';
                continue;
            }

            current += char;
        }

        values.push(current);
        return values;
    }

    function normalizeRtspPath(path: string): string {
        const trimmed = path.trim();
        if (!trimmed) {
            return '';
        }
        return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    }

    function parseProtocol(value: string): LocationModel.RtspProtocol {
        const normalized = value.trim().toUpperCase();
        switch (normalized) {
            case 'TCP':
                return LocationModel.RtspProtocol.TCP;
            case 'RTSPS':
            case 'SECURED':
                return LocationModel.RtspProtocol.SECURED;
            case 'UDP':
            default:
                return LocationModel.RtspProtocol.UDP;
        }
    }
}
